import React, { useState, MouseEvent } from "react";
import { CellProps } from "react-table";
import SupplyPortal from "../../../../SupplyPortal";
import getFontAwesomeIcon from "../../../../../../utils/getFontAwesomeIcon";

export interface TableTooltipCellProps extends CellProps<any> {
	tableData: any[];
	cellClickAction?: (e: MouseEvent, uuid: string) => void;
	icon: string;
	weight?: "solid" | "regular" | "light";
	position?: "top" | "bottom" | "left" | "right";
	tipText: string;
}

const TableTooltipCell: React.FC<TableTooltipCellProps> = ({
	value,
	row,
	tableData,
	cellClickAction,
	icon,
	weight = "solid",
	position = "top",
	tipText = "Click for more info",
}) => {
	const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
	const rowIndex = row.index;
	const rowUuid = tableData[rowIndex]?.data?.uuid ?? "";

	const onMouseEnter = (e: MouseEvent<HTMLDivElement>) => {
		setHoverRect(e.currentTarget.getBoundingClientRect());
	};
	const onMouseLeave = () => setHoverRect(null);

	// Compute tooltip placement
	const getTooltipStyle = (): React.CSSProperties => {
		if (!hoverRect) return {};
		const scrollY = window.scrollY;
		const scrollX = window.scrollX;
		const centerX = hoverRect.left + hoverRect.width / 2 + scrollX;
		const centerY = hoverRect.top + hoverRect.height / 2 + scrollY;

		let style: React.CSSProperties = { position: "fixed" };
		switch (position) {
			case "top":
				style.top = hoverRect.top - 8 + scrollY;
				style.left = centerX;
				style.transform = "translate(-50%, -100%)";
				break;
			case "bottom":
				style.top = hoverRect.bottom + 8 + scrollY;
				style.left = centerX;
				style.transform = "translate(-50%, 0)";
				break;
			case "left":
				style.left = hoverRect.left - 8 + scrollX;
				style.top = centerY;
				style.transform = "translate(-100%, -50%)";
				break;
			case "right":
				style.left = hoverRect.right + 8 + scrollX;
				style.top = centerY;
				style.transform = "translate(0, -50%)";
				break;
		}
		return style;
	};

	return (
		<div
			onClick={(e) => {
				if (typeof cellClickAction === "function") {
					e.stopPropagation();
					cellClickAction(e, rowUuid);
				}
			}}
			className="flex items-center relative z-[2] "
		>
			<p>{value}</p>
			<div
				className="pl-2 text-primary hover:text-supply-blue-300 cursor-pointer"
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{getFontAwesomeIcon(icon, weight)}
			</div>

			{hoverRect && (
				<SupplyPortal containerId="portal-root">
					<div
						style={getTooltipStyle()}
						className="font-omnes-medium text-sm  p-1 bg-navy-400 text-white rounded pointer-events-none whitespace-nowrap z-[9999]"
					>
						{tipText}
					</div>
				</SupplyPortal>
			)}
		</div>
	);
};

export default TableTooltipCell;
